import { CommonModule } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    APP_INITIALIZER,
    DEFAULT_CURRENCY_CODE,
    ErrorHandler,
    LOCALE_ID,
    NgModule,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { AuthModule } from '@auth0/auth0-angular';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import {
    SystemApiService,
    UserFacadeService,
} from '@wdx/clmi/api-services/services';
import {
    DynamicFormsManagementService,
    ModalManagementService,
} from '@wdx/clmi/ui';
import { SystemSettingsService } from '@wdx/clmi/utils/services';
import { WdxDialogService } from '@wdx/shared/components/wdx-dialog';
import { WdxToastComponent } from '@wdx/shared/components/wdx-toast';
import { ApiPaginationService } from '@wdx/shared/infrastructure/api-service';
import {
    CustomRouteSerializer,
    SITEMAP_MATCHER_SERVICE,
} from '@wdx/shared/infrastructure/state';
import {
    IMeLocale,
    IThemeService,
    LocalStorageService,
    WdxAppThemeService,
    WINDOW_PROVIDERS,
} from '@wdx/shared/utils';
import { MarkdownModule } from 'ngx-markdown';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MoleculeLoaderOverlayModule } from './components/molecules/molecule-loader-overlay/molecule-loader-overlay.module';
import { OrganismDynamicFormModalModule } from './components/organisms/organism-dynamic-form-modal/organism-dynamic-form-modal.module';
import { OrganismAuditDiffModalComponent } from './components/organisms/organism-audit-diff-modal/organism-audit-diff-modal.component';
import { OrganismFormHistoryModalComponent } from './components/organisms/organism-form-history-modal/organism-form-history-modal.component';
import { OrganismEngagementBarModule } from './components/organisms/organism-engagement-bar/organism-engagement-bar.module';
import { OrganismInfoCardCheckDuplicationsModalModule } from './components/organisms/organism-info-card-check-duplications-modal/organism-info-card-check-duplications-modal.module';
import { GlobalErrorHandler } from './global-error-handler';
import { AcceptedLanguageInterceptor } from './interceptors/accepted-language.interceptor';
import { ApiErrorInterceptor } from './interceptors/api-error.interceptor';
import { AuthenticationInterceptor } from './interceptors/authentication.interceptor';
import { DuplicateDetectionInterceptor } from './interceptors/duplicate-detection.interceptor';
import { Http500ErrorInterceptor } from './interceptors/http-500-error.interceptor';
import { HttpErrorDecodeInterceptor } from './interceptors/http-error-decode.interceptor';
import { ToastInterceptor } from './interceptors/toast.interceptor';
import { MappersModule } from './pipes/mappers/mappers.module';
import { PipesModule } from './pipes/pipes.module';
import { AuthenticationFactory } from './services/authentication.factor';
import { AuthenticationService } from './services/authentication.service';
import { ConfigService } from './services/config.service';
import { EncodedQueryParamsService } from './services/encoded-query-params.service';
import { OffcanvasManagementService } from './services/offcanvas-management.service';
import { SignalRService } from './services/signalr.service';
import { AccessVisibilityModule } from './shared/features/access-visibility/access-visibility.module';
import { CommentsModule } from './shared/features/comments';
import { MessageModule } from './shared/features/message/message.module';
import { ConsentService } from './shared/services/consent';
import { WdxSitemapMatcherService } from './shared/services/navigation/sitemap-matcher.service';
import { globalEffects as rootEffects } from './state/_setup/effects';
import * as rootReducer from './state/_setup/reducers';
import { rootServices } from './state/_setup/services';
import { PushPipe } from '@ngrx/component';

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        AccessVisibilityModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        CommonModule,
        MappersModule,
        MessageModule,
        PipesModule,
        ReactiveFormsModule,
        StoreModule.forRoot(rootReducer.reducers),
        EffectsModule.forRoot(rootEffects),
        StoreDevtoolsModule.instrument({
            maxAge: 500,
            logOnly: environment.production,
            connectInZone: true,
        }),
        StoreRouterConnectingModule.forRoot({
            serializer: CustomRouteSerializer,
        }),
        NgbModule,
        MarkdownModule.forRoot(),
        AuthModule.forRoot(),
        OrganismInfoCardCheckDuplicationsModalModule,
        CommentsModule,
        MoleculeLoaderOverlayModule,
        OrganismDynamicFormModalModule,
        OrganismAuditDiffModalComponent,
        OrganismFormHistoryModalComponent,
        OrganismEngagementBarModule,
        WdxToastComponent,
        PushPipe,
    ],
    providers: [
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (configService: ConfigService) => {
                return () => configService.load();
            },
            deps: [ConfigService, Sentry.TraceService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (consentService: ConsentService) => {
                return () => consentService.getConsentParams();
            },
            deps: [ConsentService],
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useValue: 'en-GB',
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useValue: 'GBP',
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ToastInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthenticationInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DuplicateDetectionInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AcceptedLanguageInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: Http500ErrorInterceptor,
            multi: true,
        },
        // ensure this interceptor is the last imported - that will make it the first to be called when
        // we get a response, it decodes the error response if it is a Blob
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorDecodeInterceptor,
            multi: true,
        },
        {
            provide: AuthenticationService,
            useFactory: AuthenticationFactory,
            deps: [
                Router,
                ConfigService,
                LocalStorageService,
                SystemSettingsService,
                SystemApiService,
            ],
        },
        WdxAppThemeService,
        {
            provide: IThemeService,
            useExisting: WdxAppThemeService,
        },
        ApiPaginationService,
        ConfigService,
        DynamicFormsManagementService,
        EncodedQueryParamsService,
        ModalManagementService,
        OffcanvasManagementService,
        SignalRService,
        WdxDialogService,
        WINDOW_PROVIDERS,
        ...rootServices,
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: SITEMAP_MATCHER_SERVICE,
            useClass: WdxSitemapMatcherService,
        },
        {
            provide: IMeLocale,
            useExisting: UserFacadeService,
        },
    ],
})
export class AppModule {}
